/**
 * Pure countdown math for the operator free-trial indicator (roadmap #11).
 *
 * Extracted from the byte-identical `computeTrialMsLeft` helper that previously
 * lived, duplicated, in BOTH routes/billing.tsx and routes/welcome.tsx, so the
 * trial estimate is now a single, unit-testable source of truth with NO
 * React/Firestore dependency. Callers pass a live `nowMs` (`Date.now()`) — this
 * module never reads the clock itself, which is exactly what makes it
 * deterministically testable (mirrors past-due-countdown.ts).
 *
 * A trial is a POSITIVE/NEUTRAL state, not a dunning one: the copy assembled
 * here is calm, and the UI only shifts to a gentle nudge inside the final
 * stretch (`imminent`). It never alarms.
 */

import type { SubscriptionState } from '@kilat/shared';

const DAY_MS = 86_400_000;

/**
 * Whole-days-left threshold at or below which the trial is "imminent" — the
 * banner shifts from its calm brand-tinted look to a gentle amber nudge. Three
 * days is the "final stretch" window: long enough for an operator to add
 * billing without pressure, short enough that the nudge feels timely. Never
 * escalates to a red/alarming tone (that is reserved for suspension).
 */
export const TRIAL_IMMINENT_DAYS = 3;

/**
 * Read the trial-end epoch ms off a subscription, or null when there is no
 * active trial to measure. Handles both a real Firestore `Timestamp` (has
 * `toMillis()`) and a bare `{ seconds, nanoseconds }` fixture (seed/prerender/
 * test) — identical to the two originals this replaces.
 */
function trialEndMs(sub: SubscriptionState | null): number | null {
  if (!sub) return null;
  if (sub.status !== 'trial') return null;
  const endsAt = sub.trialEndsAt;
  if (!endsAt) return null;
  return typeof endsAt.toMillis === 'function' ? endsAt.toMillis() : endsAt.seconds * 1000;
}

/**
 * Milliseconds remaining on an active free trial, or null.
 *
 * Returns null unless the subscription is genuinely on a `trial` WITH a
 * `trialEndsAt` set; otherwise `Math.max(0, endMs - nowMs)` — floored at 0 so a
 * lapsed-but-not-yet-swept trial reports 0, never a negative value.
 *
 * @param sub    subscription state (nullable — pre-load / read not permitted)
 * @param nowMs  current epoch ms (live clock, passed in — never read here)
 */
export function computeTrialMsLeft(sub: SubscriptionState | null, nowMs: number): number | null {
  const endMs = trialEndMs(sub);
  if (endMs === null) return null;
  return Math.max(0, endMs - nowMs);
}

export interface TrialCountdown {
  /** Epoch ms at which the trial ends (the raw `trialEndsAt`, un-floored). */
  readonly endMs: number;
  /** Milliseconds remaining until the trial ends, floored at 0. */
  readonly msLeft: number;
  /**
   * Whole days remaining, rounded to the NEAREST day (`Math.round`, matching
   * the pre-existing billing.tsx / welcome.tsx display). `0` means the trial
   * window has effectively elapsed but the daily lifecycle sweep hasn't yet
   * flipped `trial → active`.
   */
  readonly daysLeft: number;
  /**
   * True when `daysLeft <= TRIAL_IMMINENT_DAYS` — the UI shows a gentle amber
   * nudge instead of the calm brand tint. Still never alarming.
   */
  readonly imminent: boolean;
}

/**
 * Full trial countdown for an active trial, or null when there is no active
 * trial (the caller then renders nothing).
 *
 * @param sub    subscription state
 * @param nowMs  current epoch ms (live clock, passed in)
 */
export function computeTrialCountdown(
  sub: SubscriptionState | null,
  nowMs: number,
): TrialCountdown | null {
  const endMs = trialEndMs(sub);
  if (endMs === null) return null;
  const msLeft = Math.max(0, endMs - nowMs);
  const daysLeft = Math.max(0, Math.round(msLeft / DAY_MS));
  return { endMs, msLeft, daysLeft, imminent: daysLeft <= TRIAL_IMMINENT_DAYS };
}

/** The subset of `useI18n().t` this helper needs (key + interpolation vars). */
export type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Assemble the human trial line from a countdown + a translate function.
 *
 * Extracted (like `assembleCountdownLine` in past-due-countdown.ts) so the i18n
 * VAR NAMES are unit-tested against the real catalog: a var/placeholder
 * mismatch (e.g. passing `{ n }` to a `"{{days}}"` string) silently renders a
 * literal "{{days}}" to the operator, which a React-free harness can't
 * otherwise catch. Returns null when there is no active trial (the caller then
 * hides the line).
 */
export function assembleTrialLine(
  countdown: TrialCountdown | null,
  t: TranslateFn,
  formatDate: (ms: number) => string,
): string | null {
  if (!countdown) return null;
  // Window elapsed (or in its last hours) — a gentle "ended" variant, never
  // alarming, still pointing at the single next action (add billing).
  if (countdown.daysLeft === 0) return t('dashboard.trial.ended');
  const daysLabel =
    countdown.daysLeft === 1
      ? t('dashboard.trial.daysOne')
      : t('dashboard.trial.daysOther', { days: countdown.daysLeft });
  return t('dashboard.trial.line', {
    days: daysLabel,
    date: formatDate(countdown.endMs),
  });
}
